import { useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useApplicationContext } from 'contexts/ApplicationContext';
import i18nContext from 'components/i18n-context';
import {
  APPROVAL_STATUS,
  COMMON_CURRENCY_PRECISION,
  CRYPTO_CURRENCY_PRECISION,
  PAYMENT_METHOD,
  PAYMENT_PROVIDER,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES as TRANSACTION_TYPE,
  TRANSFER_TYPE
} from 'components/constants';
import { getCustomerInfoRequest } from 'services/requestAgent';
import { hasRole, TRANSACTION_CREATE, TRANSACTION_UPDATE } from 'services/roles';
import {
  formatDateTime,
  formatMoney,
  getCustomerLink,
  getPaymentMethodClass,
  getTransactionDirectionIcon,
  getTransactionStatusClass,
  isExchangeTransaction,
  isCryptoProvider
} from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import Button from 'uikit/Button/Button';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import NotesTab from 'components/NotesTab';
import './DetailsTab.scss';

const DetailsTab = ({
  isLoading,
  transactionData,
  transactionNumber,
  notes,
  handleAcceptOnHold,
  handleAcceptProcessing,
  handleReject,
  handleCreateIncomingExchange,
  handleReturn,
  handleRetry,
  handleSaveMlroApproval,
  handleBoostCryptoTransaction,
  handleUnfreezeCryptoTransaction,
  handleAcceptDelayedTransaction,
  handleRejectDelayedTransaction,
  handleLoadNotes,
  handleAddNote,
  handleDeleteNote,
  handleDownloadDocument
}) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const navigate = useNavigate();
  const initialedDate = transactionData ? formatDateTime(transactionData?.createdAt) : null;
  const updatedDate = transactionData ? formatDateTime(transactionData?.updatedAt) : null;
  const completedDate = transactionData ? formatDateTime(transactionData?.completedAt) : null;
  const [mlroApproval, setMlroApproval] = useState(
    transactionData?.mlroApproval?.status ? transactionData.mlroApproval.status : ''
  );
  const transactionAmount =
    transactionData.transactionType === TRANSACTION_TYPE.OUTGOING
      ? transactionData?.targetAmount
      : transactionData.sourceAmount;

  const mlroApprovalStatusOptions = Object.values(APPROVAL_STATUS).map((value) => ({
    key: value,
    value: i18n.getMessage(`mlroApprovalStatus.${value}`)
  }));

  const isExchangeProcessingTransaction = (data) => {
    if (data.status !== TRANSACTION_STATUS.PROCESSING || data.transferProvider === PAYMENT_PROVIDER.GRAPHEREX.name) {
      return false;
    }
    return data.transferType === TRANSFER_TYPE.EXCHANGE;
  };

  const isGrapherexExchangeAcceptAllowed = (data) => {
    return data.actions?.acceptGrapherexExchangeReturn;
  };

  const isGrapherexExchangeRejectAllowed = (data) => {
    return data.actions?.rejectGrapherexExchangeReturn;
  };

  const isGrapherexExchangeReturnAllowed = (data) => {
    return data.actions?.returnGrapherexExchange;
  };

  const isReturn = (data) => {
    if (
      (data.transactionType === TRANSACTION_TYPE.OUTGOING &&
        data.transferType === TRANSFER_TYPE.EXCHANGE &&
        data.paymentMethod === PAYMENT_METHOD.INTERNAL) ||
      (data.transactionType === TRANSACTION_TYPE.INCOMING && data.paymentMethod === PAYMENT_METHOD.CRYPTO) ||
      data.transferProvider === PAYMENT_PROVIDER.GRAPHEREX.name ||
      (!data.actions?.returnGrapherexExchange && data.actions?.createIncomingExchange)
    ) {
      return false;
    }

    return (
      (data.transactionType === TRANSACTION_TYPE.OUTGOING && data.transferType === TRANSFER_TYPE.EXCHANGE) ||
      (data.transactionType === TRANSACTION_TYPE.INCOMING &&
        data.paymentMethod !== PAYMENT_METHOD.INTERNAL &&
        (data.status === TRANSACTION_STATUS.ON_HOLD || data.status === TRANSACTION_STATUS.PAID))
    );
  };

  const isRetry = (data) => {
    return (
      data.actions?.retryMonitoring &&
      !(
        data.transactionType === TRANSACTION_TYPE.OUTGOING &&
        data.relatedTransactionNumber &&
        data.paymentMethod === PAYMENT_METHOD.INTERNAL
      )
    );
  };

  const isAcceptRejectDelayedTransaction = (data) => {
    return !(
      data?.status !== TRANSACTION_STATUS.PENDING ||
      data?.transactionType !== TRANSACTION_TYPE.OUTGOING ||
      !data?.delayed
    );
  };

  const handleRedirectToCustomer = async (customerNumber) => {
    const customer = await getCustomerInfoRequest(customerNumber);
    navigate(getCustomerLink(customerNumber, customer.status, customer.type));
  };

  // Repeat creates a transaction - it needs the create role, not the update one of the other actions
  const isRepeatAllowed = hasRole(TRANSACTION_CREATE) && !!transactionData?.actions?.repeat;

  const handleRepeat = () => {
    const searchParams = new URLSearchParams({ transactionNumber: transactionData.transactionNumber });
    navigate(`${ROUTE_PATHS.CREATE_REPEAT_TRANSACTION}?${searchParams.toString()}`);
  };

  const handleChangeMlroApprovalStatus = (name, status) => {
    setMlroApproval(status);
  };

  if (!transactionData) return null;

  return (
    <div className={'details-tab-wrapper'}>
      <div className={'details-tab-content'}>
        {transactionData?.delayed && (
          <div className={'delayed-transaction-warning'}>
            <span className={'delayed-transaction-warning-text'}>
              {i18n.getMessage('transactionDetails.delayed.warning')}
            </span>
          </div>
        )}

        {transactionData && (hasRole(TRANSACTION_UPDATE) || isRepeatAllowed) && (
          <div className={'transaction-details-actions-wrapper'}>
            {hasRole(TRANSACTION_UPDATE) && (
              <>
                {isAcceptRejectDelayedTransaction(transactionData) && (
                  <>
                    <Button
                      className={'transaction-details-actions-button'}
                      size={'medium'}
                      type={'primary'}
                      onClick={handleAcceptDelayedTransaction}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('transactionDetails.button.accept')}
                    </Button>

                    <Button
                      className={'transaction-details-actions-button'}
                      size={'medium'}
                      type={'primary'}
                      onClick={handleRejectDelayedTransaction}
                      isDisabled={false}
                    >
                      {i18n.getMessage('transactionDetails.button.reject')}
                    </Button>
                  </>
                )}

                {transactionData.actions?.acceptOnHold && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleAcceptOnHold}
                    isDisabled={isLoading}
                  >
                    {i18n.getMessage('transactionDetails.button.accept')}
                  </Button>
                )}

                {(transactionData.actions?.acceptRejectProcessing ||
                  isGrapherexExchangeAcceptAllowed(transactionData)) && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={() => handleAcceptProcessing()}
                    isDisabled={isLoading}
                  >
                    {i18n.getMessage('transactionDetails.button.accept')}
                  </Button>
                )}

                {isExchangeProcessingTransaction(transactionData) && (
                  <>
                    <Button
                      className={'transaction-details-actions-button'}
                      size={'medium'}
                      type={'primary'}
                      onClick={() => handleAcceptProcessing()}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('transactionDetails.button.accept')}
                    </Button>
                    <Button
                      className={'transaction-details-actions-button'}
                      size={'medium'}
                      type={'primary'}
                      onClick={handleReject}
                      isDisabled={false}
                    >
                      {i18n.getMessage('transactionDetails.button.reject')}
                    </Button>
                  </>
                )}

                {(transactionData.actions?.rejectOnHold ||
                  transactionData.actions?.acceptRejectProcessing ||
                  isGrapherexExchangeRejectAllowed(transactionData)) && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleReject}
                    isDisabled={false}
                  >
                    {i18n.getMessage('transactionDetails.button.reject')}
                  </Button>
                )}

                {(isReturn(transactionData) || isGrapherexExchangeReturnAllowed(transactionData)) && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleReturn}
                    isDisabled={transactionData?.relatedTransactionNumber}
                  >
                    {transactionData?.relatedTransactionNumber &&
                    transactionData.transferProvider !== PAYMENT_PROVIDER.GRAPHEREX.name
                      ? i18n.getMessage('transactionDetails.button.returned')
                      : i18n.getMessage('transactionDetails.button.return')}
                  </Button>
                )}

                {isRetry(transactionData) && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleRetry}
                    isDisabled={transactionData?.relatedTransactionNumber}
                  >
                    {i18n.getMessage('transactionDetails.button.retry')}
                  </Button>
                )}

                {transactionData.actions?.createIncomingExchange && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleCreateIncomingExchange}
                    isDisabled={false}
                  >
                    {i18n.getMessage('transactionDetails.button.createIncomingExchange')}
                  </Button>
                )}

                {transactionData.actions?.boostCryptoTransaction && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleBoostCryptoTransaction}
                    isDisabled={isLoading}
                  >
                    {i18n.getMessage('transactionDetails.button.boost')}
                  </Button>
                )}

                {transactionData.actions?.unfreezeCryptoTransaction && (
                  <Button
                    className={'transaction-details-actions-button'}
                    size={'medium'}
                    type={'primary'}
                    onClick={handleUnfreezeCryptoTransaction}
                    isDisabled={isLoading}
                  >
                    {i18n.getMessage('transactionDetails.button.unfreeze')}
                  </Button>
                )}
              </>
            )}

            {isRepeatAllowed && (
              <Button
                className={'transaction-details-actions-button'}
                size={'medium'}
                type={'primary'}
                onClick={handleRepeat}
                isDisabled={isLoading}
              >
                {i18n.getMessage('transactionDetails.button.repeat')}
              </Button>
            )}
          </div>
        )}

        <div className={'transaction-details-tables-wrapper'}>
          <table className={'table-wrapper'}>
            <tbody className={'table-body'}>
              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.transactionNumber')}
                </td>
                <td className={'table-cell'}>{transactionData.transactionNumber}</td>
              </tr>

              {transactionData.relatedTransactionNumber && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.field.relatedTransactionNumber')}
                  </td>
                  <td className={'table-cell'}>
                    <NavLink
                      className={'related-transaction-number'}
                      to={`${ROUTE_PATHS.TRANSACTIONS}/${transactionData.relatedTransactionNumber}`}
                    >
                      {transactionData.relatedTransactionNumber}
                    </NavLink>
                  </td>
                </tr>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.customerId')}</td>
                <td className={'table-cell'}>
                  <Button
                    className={'link-button nav-link-text'}
                    onClick={async () => await handleRedirectToCustomer(transactionData.accountNumber)}
                    isDisabled={false}
                  >
                    {transactionData.accountNumber}
                  </Button>
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.obligationId')}</td>
                <td className={'table-cell'}>{transactionData.paymentObligationId}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.createdAt')}</td>
                <td className={'table-cell'}>{initialedDate}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.updatedAt')}</td>
                <td className={'table-cell'}>{updatedDate}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.completedAt')}</td>
                <td className={'table-cell'}>{completedDate}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.transferProvider')}
                </td>
                <td className={'table-cell'}>
                  {i18n.getMessage(`paymentProvider.${transactionData.transferProvider}`)}
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.transferType')}</td>
                <td className={'table-cell'}>
                  <span
                    className={
                      isExchangeTransaction(transactionData.transferType) ? 'transaction-exchange-highlight' : ''
                    }
                  >
                    {i18n.getMessage(`transferType.${transactionData.transferType}`)}
                  </span>
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.transactionType')}
                </td>
                <td className={'table-cell'}>
                  <span>
                    <span className={'transaction-direction-icon'}>
                      {getTransactionDirectionIcon(transactionData.transactionType)}{' '}
                    </span>
                    {i18n.getMessage(`transactionType.${transactionData.transactionType}`)}
                  </span>
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.paymentMethod')}</td>
                <td className={'table-cell'}>
                  <span className={getPaymentMethodClass(transactionData.paymentMethod)}>
                    {i18n.getMessage(`paymentMethod.${transactionData.paymentMethod}`)}
                  </span>
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.transactionStatus')}
                </td>
                <td className={'table-cell'}>
                  <span className={getTransactionStatusClass(transactionData.status)}>
                    {i18n.getMessage(`transaction.status.${transactionData.status}`)}
                  </span>
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.tariffType')}</td>
                <td className={'table-cell'}>{transactionData?.tariffType}</td>
              </tr>

              {transactionData?.tariffType === 'CARD_MONTHLY_FEE_VIRTUAL' && (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.cardId')}</td>
                    <td className={'table-cell'}>{transactionData?.card_id}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.cardNumberMasked')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.card_number_masked}</td>
                  </tr>
                </>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.externalReference')}
                </td>
                <td className={'table-cell'}>{transactionData.externalReference}</td>
              </tr>

              {transactionData?.apiVersion && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.apiVersion')}</td>
                  <td className={'table-cell'}>{transactionData.apiVersion}</td>
                </tr>
              )}

              {transactionData?.cryptoHash && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.cryptoHash')}</td>
                  <td className={'table-cell'}>{transactionData.cryptoHash}</td>
                </tr>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.amount')}</td>
                <td className={'table-cell'}>
                  {formatMoney(
                    transactionAmount,
                    isCryptoProvider(transactionData.transferProvider)
                      ? CRYPTO_CURRENCY_PRECISION
                      : COMMON_CURRENCY_PRECISION
                  )}
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.currency')}</td>
                <td className={'table-cell'}>{transactionData.currency}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.exchangeRate')}</td>
                <td className={'table-cell'}>{transactionData.appliedRate?.rate || null}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.commission')}</td>
                <td className={'table-cell'}>
                  {formatMoney(
                    transactionData.commission,
                    isCryptoProvider(transactionData.transferProvider)
                      ? CRYPTO_CURRENCY_PRECISION
                      : COMMON_CURRENCY_PRECISION
                  )}
                </td>
              </tr>

              {transactionData.commissions?.length > 0 && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.field.commissionBreakdown')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'commission-breakdown-wrapper'}>
                      {transactionData.commissions.map((commission, index) => (
                        <div key={index} className={'commission-breakdown-item'}>
                          <div className={'commission-breakdown-item-header'}>
                            <span className={'commission-breakdown-item-amount'}>
                              {formatMoney(
                                commission.amount,
                                isCryptoProvider(transactionData.transferProvider)
                                  ? CRYPTO_CURRENCY_PRECISION
                                  : COMMON_CURRENCY_PRECISION
                              )}
                            </span>
                            {commission.details?.highRiskCountries && (
                              <span className={'commission-breakdown-high-risk-badge'}>
                                {i18n.getMessage('transactionDetails.field.commissionBreakdown.highRiskCountry')}
                              </span>
                            )}
                          </div>
                          <div className={'commission-breakdown-item-details'}>
                            {commission.details?.feeType && (
                              <span>
                                {i18n.getMessage('transactionDetails.field.commissionBreakdown.feeType')}:{' '}
                                {commission.details.feeType}
                                {commission.details.feeType === 'FIXED' &&
                                  commission.details.fixedValue != null &&
                                  ` (${commission.details.fixedValue})`}
                                {commission.details.feeType === 'PERCENT' &&
                                  commission.details.percentValue != null &&
                                  ` (${commission.details.percentValue}%)`}
                              </span>
                            )}
                            {commission.details?.tariffGroupId && (
                              <span>
                                {i18n.getMessage('transactionDetails.field.commissionBreakdown.tariffGroupId')}:{' '}
                                {commission.details.tariffGroupId}
                              </span>
                            )}
                            {commission.details?.systemTariffKey && (
                              <span>
                                {i18n.getMessage('transactionDetails.field.commissionBreakdown.systemTariffKey')}:{' '}
                                {commission.details.systemTariffKey}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}

              {isCryptoProvider(transactionData.transferProvider) && (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.blockchainNetworkFee')}
                    </td>
                    <td className={'table-cell'}>
                      {formatMoney(transactionData?.blockchainNetworkFee, CRYPTO_CURRENCY_PRECISION)}
                    </td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.blockchainNetworkFeeCurrency')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.blockchainNetworkFeeCurrency}</td>
                  </tr>
                </>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.comment')}</td>
                <td className={'table-cell'}>{transactionData.comment}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.commentToAdmin')}</td>
                <td className={'table-cell'}>{transactionData.commentToAdmin}</td>
              </tr>

              {transactionData?.purpose && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.field.paymentPurpose')}
                  </td>
                  <td className={'table-cell'}>
                    {constants?.paymentPurposes?.[transactionData.purpose]
                      ? `${transactionData.purpose} - ${constants.paymentPurposes[transactionData.purpose]}`
                      : transactionData.purpose}
                  </td>
                </tr>
              )}

              {transactionData?.cardAuthorization && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.field.cardAuthorization')}
                  </td>
                  <td className={'table-cell'}>{transactionData.cardAuthorization.authorizationId}</td>
                </tr>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.errorReason')}</td>
                <td className={'table-cell'}>{transactionData.statusDetails}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.mlroApproval')}
                  <br />
                  <small>
                    <i>
                      {transactionData.mlroApproval.updatedAt && formatDateTime(transactionData.mlroApproval.updatedAt)}
                      {transactionData.mlroApproval.updatedBy && ' ' + transactionData.mlroApproval.updatedBy}
                    </i>
                  </small>
                </td>
                <td className={'table-cell'}>
                  {
                    <div className={'transaction-details-table-input-wrapper'}>
                      <InputDropDown
                        className={'table-cell-input'}
                        options={mlroApprovalStatusOptions}
                        value={mlroApproval}
                        onChange={handleChangeMlroApprovalStatus}
                        isDisabled={!hasRole(TRANSACTION_UPDATE)}
                      />
                      <Button
                        className={'transaction-details-save-button'}
                        size={'small'}
                        type={'primary'}
                        onClick={() => handleSaveMlroApproval(mlroApproval)}
                        isDisabled={!hasRole(TRANSACTION_UPDATE)}
                      >
                        {i18n.getMessage('transactionDetails.button.save')}
                      </Button>
                    </div>
                  }
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.delayed')}</td>
                <td className={'table-cell'}>
                  {transactionData?.delayed
                    ? i18n.getMessage('transactionDetails.field.delayed.yes')
                    : i18n.getMessage('transactionDetails.field.delayed.no')}
                </td>
              </tr>
            </tbody>
          </table>

          <table className={'table-wrapper'}>
            <tbody className={'table-body'}>
              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.counterpartyNumber')}
                </td>
                <td className={'table-cell'}>
                  {transactionData?.counterpartyNumber && (
                    <NavLink
                      className={'nav-link-text'}
                      to={`${ROUTE_PATHS.TRANSACTIONS}?search_text=${transactionData?.counterpartyNumber}`}
                    >
                      {transactionData?.counterpartyNumber}
                    </NavLink>
                  )}
                </td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.bankAccountHolder')}
                </td>
                <td className={'table-cell'}>{transactionData?.bankAccountHolder}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.recipientName')}</td>
                <td className={'table-cell'}>{transactionData.recipient?.partyName}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.recipientAddress')}
                </td>
                <td className={'table-cell'}>{transactionData?.recipient?.partyAddress}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.recipientCity')}</td>
                <td className={'table-cell'}>{transactionData?.recipient?.partyCity}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.recipientPostCode')}
                </td>
                <td className={'table-cell'}>{transactionData?.recipient?.partyPostCode}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>
                  {i18n.getMessage('transactionDetails.field.recipientCountry')}
                </td>
                <td className={'table-cell'}>{transactionData?.recipient?.partyCountry}</td>
              </tr>

              {transactionData?.recipient?.partyState && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.field.recipientState')}
                  </td>
                  <td className={'table-cell'}>{transactionData.recipient.partyState}</td>
                </tr>
              )}

              {isCryptoProvider(transactionData.transferProvider) ? (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientCryptoWalletAddress')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.cryptoWalletAddress}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientWalletNetwork')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.network}</td>
                  </tr>
                </>
              ) : (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientIban')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.iban}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientAccountNumber')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.accountNumber}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientSortCode')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.sortCode}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientBank')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.bankName}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientBankCountry')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.bankCountry}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientBankAddress')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.bankAddress}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientRegistrationNumber')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.partyRegistrationNumber}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.recipientBankSwiftCode')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.recipient?.bic}</td>
                  </tr>
                </>
              )}

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.senderName')}</td>
                <td className={'table-cell'}>{transactionData.sender?.partyName}</td>
              </tr>

              <tr className={'table-row'}>
                <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.senderCountry')}</td>
                <td className={'table-cell'}>{transactionData.sender?.partyCountry}</td>
              </tr>

              {transactionData?.sender?.partyState && (
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.senderState')}</td>
                  <td className={'table-cell'}>{transactionData.sender?.partyState}</td>
                </tr>
              )}

              {isCryptoProvider(transactionData.transferProvider) ? (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderCryptoWalletAddress')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.sender?.cryptoWalletAddress}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderWalletNetwork')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.sender?.network}</td>
                  </tr>
                </>
              ) : (
                <>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.senderIban')}</td>
                    <td className={'table-cell'}>{transactionData.sender?.iban}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderAccountNumber')}
                    </td>
                    <td className={'table-cell'}>{transactionData.sender?.accountNumber}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.field.senderBank')}</td>
                    <td className={'table-cell'}>{transactionData.sender?.bankName}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderBankCountry')}
                    </td>
                    <td className={'table-cell'}>{transactionData.sender?.bankCountry}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderBankAddress')}
                    </td>
                    <td className={'table-cell'}>{transactionData?.sender?.bankAddress}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.field.senderBankSwiftCode')}
                    </td>
                    <td className={'table-cell'}>{transactionData.sender?.bic}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NotesTab
        isLoading={isLoading}
        notes={notes}
        transactionNumber={transactionNumber}
        handleLoadNotes={handleLoadNotes}
        handleAddNote={handleAddNote}
        handleDeleteNote={handleDeleteNote}
        handleDownloadDocument={handleDownloadDocument}
      />
    </div>
  );
};

DetailsTab.propTypes = {
  isLoading: PropTypes.bool,
  transactionData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  transactionNumber: PropTypes.string,
  notes: PropTypes.array,
  handleAcceptOnHold: PropTypes.func,
  handleAcceptProcessing: PropTypes.func,
  handleReject: PropTypes.func,
  handleReturn: PropTypes.func,
  handleRetry: PropTypes.func,
  handleChangeDocumentStatus: PropTypes.func,
  handleCreateIncomingExchange: PropTypes.func,
  handleSaveMlroApproval: PropTypes.func,
  handleOpenAttachDocumentPopUp: PropTypes.func,
  downloadTransactionDocument: PropTypes.func,
  deleteTransactionDocument: PropTypes.func,
  handleAcceptDelayedTransaction: PropTypes.func,
  handleRejectDelayedTransaction: PropTypes.func,
  handleBoostCryptoTransaction: PropTypes.func,
  handleUnfreezeCryptoTransaction: PropTypes.func,
  handleLoadNotes: PropTypes.func,
  handleAddNote: PropTypes.func,
  handleDeleteNote: PropTypes.func,
  handleDownloadDocument: PropTypes.func
};

export default DetailsTab;
